import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles, colors } from '../../styles/globalStyles';

const DIAS = [
  { id: 1, nome: 'Segunda-feira', abrev: 'Seg' },
  { id: 2, nome: 'Terça-feira',   abrev: 'Ter' },
  { id: 3, nome: 'Quarta-feira',  abrev: 'Qua' },
  { id: 4, nome: 'Quinta-feira',  abrev: 'Qui' },
  { id: 5, nome: 'Sexta-feira',   abrev: 'Sex' },
  { id: 6, nome: 'Sábado',        abrev: 'Sáb' },
];

const HORARIOS = [
  '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00',
];

const LIMITE_SLOT  = 6; // máx alunos fixos por slot (dia+hora)
const MAX_SEMANA   = 3; // máx aulas fixas por semana por aluno
const MAX_DIA      = 1; // máx aulas fixas por dia por aluno

function normalizeHorario(h) {
  if (!h) return '';
  return String(h).substring(0, 5);
}

// ─── Estilos auxiliares ───────────────────────────────────────────────────────
const mkBadge = (backgroundColor, color, borderColor) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '0.72rem',
  fontWeight: '700',
  backgroundColor,
  color,
  border: borderColor ? `1px solid ${borderColor}` : 'none',
  whiteSpace: 'nowrap',
});

const alertStyle = (type) => ({
  padding: '0.75rem 1rem',
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  marginBottom: '1rem',
  ...(type === 'error'
    ? { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }
    : { backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #86efac' }),
});

// ─── Helpers de semana ───────────────────────────────────────────────────────
const segundaDaSemana = (ref) => {
  const d = new Date(ref);
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDias = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const fmtData = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const fmtISO  = (d) => d.toISOString().split('T')[0];

// ─── Componente principal ─────────────────────────────────────────────────────
export const GerenciadorAlunosFixos = ({ createApi, isMobile = false }) => {
  const [alunosFixos, setAlunosFixos]           = useState([]);
  const [resultadosBusca, setResultadosBusca]   = useState([]);
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [buscaTexto, setBuscaTexto]             = useState('');
  const [loading, setLoading]                   = useState(false);
  const [loadingCelula, setLoadingCelula]       = useState(null);
  const [erro, setErro]                         = useState('');
  const [sucesso, setSucesso]                   = useState('');

  // estados do modal de aplicar fixos
  const [modalAplicarAberto, setModalAplicarAberto] = useState(false);
  const [semanaRef, setSemanaRef]                   = useState(() => segundaDaSemana(new Date()));
  const [loadingAplicar, setLoadingAplicar]         = useState(false);
  const [resultadoAplicar, setResultadoAplicar]     = useState(null);

  // ── Carrega todos os alunos fixos ──────────────────────────────────────────
  const carregarTodos = useCallback(async () => {
    setLoading(true);
    try {
      const api = createApi();
      const resp = await api.get('/admin/alunos-fixos');
      setAlunosFixos(resp.data);
    } catch {
      setErro('Erro ao carregar alunos fixos');
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => { carregarTodos(); }, [carregarTodos]);

  // ── Busca com debounce ─────────────────────────────────────────────────────
  useEffect(() => {
    if (buscaTexto.length < 2) { setResultadosBusca([]); return; }
    const timer = setTimeout(async () => {
      try {
        const api = createApi();
        const resp = await api.get(`/admin/alunos?q=${encodeURIComponent(buscaTexto)}`);
        setResultadosBusca(resp.data);
      } catch { /* silent */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [buscaTexto, createApi]);

  // ── Fixos do aluno selecionado ─────────────────────────────────────────────
  const fixosDoAluno = useMemo(() => {
    if (!alunoSelecionado) return [];
    return alunosFixos.filter(af => Number(af.aluno_id) === Number(alunoSelecionado.id));
  }, [alunosFixos, alunoSelecionado]);

  // Dias que já têm aula fixa (para bloquear outro slot no mesmo dia)
  const diasComFixo = useMemo(
    () => new Set(fixosDoAluno.map(af => Number(af.dia_semana))),
    [fixosDoAluno]
  );

  const semanaCheia = fixosDoAluno.length >= MAX_SEMANA;

  // ── Mapa de ocupação por slot ──────────────────────────────────────────────
  const slotMap = useMemo(() => {
    const map = {};
    DIAS.forEach(d => {
      map[d.id] = {};
      HORARIOS.forEach(h => {
        const registros = alunosFixos.filter(
          af => Number(af.dia_semana) === d.id && normalizeHorario(af.horario) === h
        );
        const fixoDoAluno = alunoSelecionado
          ? registros.find(af => Number(af.aluno_id) === Number(alunoSelecionado.id))
          : null;
        map[d.id][h] = {
          count: registros.length,
          alunoFixoId: fixoDoAluno?.id ?? null,
          temOutros: registros.some(af => Number(af.aluno_id) !== Number(alunoSelecionado?.id)),
        };
      });
    });
    return map;
  }, [alunosFixos, alunoSelecionado]);

  // ── Agrupamento por pessoa (listagem geral) ────────────────────────────────
  const alunosAgrupados = useMemo(() => {
    const map = {};
    alunosFixos.forEach(af => {
      const key = af.aluno_id;
      if (!map[key]) {
        map[key] = { id: af.aluno_id, nome: af.aluno_nome, email: af.aluno_email, auth_type: af.aluno_auth_type, password_set: af.aluno_password_set, horarios: [] };
      }
      map[key].horarios.push({
        id: af.id,
        dia_semana: Number(af.dia_semana),
        horario: normalizeHorario(af.horario),
      });
    });
    return Object.values(map).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [alunosFixos]);

  // ── Reenviar ativação / resetar senha ────────────────────────────────────
  const handleReenviarAtivacao = useCallback(async (aluno) => {
    setErro('');
    setSucesso('');
    try {
      const api = createApi();
      const res = await api.post(`/admin/usuarios/${aluno.id}/reenviar-ativacao`);
      setSucesso(res.data.message);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao enviar e-mail');
    }
  }, [createApi]);

  // ── Aplicar alunos fixos à semana ─────────────────────────────────────────
  const handleAplicarFixos = useCallback(async () => {
    setLoadingAplicar(true);
    setResultadoAplicar(null);
    try {
      const api = createApi();
      const res = await api.post('/admin/aplicar-alunos-fixos', {
        data_inicio: fmtISO(semanaRef)
      });
      setResultadoAplicar(res.data);
      if (res.data.aplicados?.length > 0) {
        await carregarTodos();
      }
    } catch (err) {
      setResultadoAplicar({ error: err.response?.data?.error || 'Erro ao aplicar alunos fixos' });
    } finally {
      setLoadingAplicar(false);
    }
  }, [createApi, semanaRef, carregarTodos]);

  const abrirModalAplicar = useCallback(() => {
    setSemanaRef(segundaDaSemana(new Date()));
    setResultadoAplicar(null);
    setModalAplicarAberto(true);
  }, []);

  const fecharModalAplicar = useCallback(() => {
    setModalAplicarAberto(false);
    setResultadoAplicar(null);
  }, []);

  const navSemanaAnterior = useCallback(() =>
    setSemanaRef(prev => addDias(prev, -7)), []);
  const navSemanaProxima = useCallback(() =>
    setSemanaRef(prev => addDias(prev, 7)), []);

  // ── Ações ──────────────────────────────────────────────────────────────────
  const selecionarAluno = (aluno) => {
    setAlunoSelecionado(aluno);
    setBuscaTexto('');
    setResultadosBusca([]);
    setErro('');
    setSucesso('');
  };

  const limparAluno = () => {
    setAlunoSelecionado(null);
    setBuscaTexto('');
    setErro('');
    setSucesso('');
  };

  const handleCelulaClick = async (diaId, horario) => {
    if (!alunoSelecionado) return;
    const celula = slotMap[diaId][horario];
    setErro('');
    setSucesso('');

    // ── Validações ao ADICIONAR ──
    if (!celula.alunoFixoId) {
      if (celula.count >= LIMITE_SLOT) {
        setErro('Slot cheio — limite de alunos fixos por horário atingido.');
        return;
      }
      if (semanaCheia) {
        setErro(`Limite semanal atingido — máximo de ${MAX_SEMANA} aulas fixas por semana.`);
        return;
      }
      if (diasComFixo.has(diaId)) {
        setErro('Já existe uma aula fixa neste dia da semana — máximo de 1 aula fixa por dia.');
        return;
      }
    }

    const chave = `${diaId}-${horario}`;
    setLoadingCelula(chave);

    try {
      const api = createApi();
      const diaNome = DIAS.find(d => d.id === diaId)?.nome ?? '';

      if (celula.alunoFixoId) {
        await api.delete(`/admin/aluno-fixo/${celula.alunoFixoId}`);
        setSucesso(`Removido: ${diaNome} às ${horario}`);
      } else {
        await api.post('/admin/aluno-fixo', {
          aluno_id: alunoSelecionado.id,
          dia_semana: diaId,
          horario,
        });
        setSucesso(`Adicionado: ${diaNome} às ${horario}`);
      }
      await carregarTodos();
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao atualizar');
    } finally {
      setLoadingCelula(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const sabado = addDias(semanaRef, 5);

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <h3 style={{ marginBottom: '1.5rem', color: colors.dark, fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
        🎯 Gerenciar Alunos Fixos
      </h3>

      {erro   && <div style={alertStyle('error')}>{erro}</div>}
      {sucesso && <div style={alertStyle('success')}>{sucesso}</div>}

      {/* ── Modal Aplicar Fixos ── */}
      {modalAplicarAberto && (
        <ModalAplicarFixos
          semanaRef={semanaRef}
          sabado={sabado}
          totalFixos={alunosFixos.length}
          loadingAplicar={loadingAplicar}
          resultadoAplicar={resultadoAplicar}
          onNavAnterior={navSemanaAnterior}
          onNavProxima={navSemanaProxima}
          onAplicar={handleAplicarFixos}
          onFechar={fecharModalAplicar}
          isMobile={isMobile}
        />
      )}

      {/* ── FASE 1: Busca / Card do aluno selecionado ── */}
      <div style={{ ...styles.card, marginBottom: '1.5rem', padding: isMobile ? '1rem' : '1.5rem' }}>
        {alunoSelecionado ? (
          <AlunoCard
            aluno={alunoSelecionado}
            totalFixos={fixosDoAluno.length}
            maxSemana={MAX_SEMANA}
            onTrocar={limparAluno}
            isMobile={isMobile}
          />
        ) : (
          <BuscaAluno
            buscaTexto={buscaTexto}
            setBuscaTexto={setBuscaTexto}
            resultados={resultadosBusca}
            onSelecionar={selecionarAluno}
            isMobile={isMobile}
          />
        )}
      </div>

      {/* ── FASE 2: Grade semanal ── */}
      {alunoSelecionado && (
        <div style={{ ...styles.card, marginBottom: '1.5rem', padding: isMobile ? '1rem' : '1.5rem' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <h4 style={{ margin: '0 0 0.25rem 0', color: colors.dark, fontSize: isMobile ? '1rem' : '1.1rem' }}>
              Horários Semanais
            </h4>
            <p style={{ margin: 0, fontSize: '0.85rem', color: colors.gray }}>
              Clique em uma célula para marcar ou desmarcar como fixo.
              Regras: máx {MAX_SEMANA} aulas/semana e {MAX_DIA} aula/dia.
            </p>
          </div>

          {/* Barra de progresso semanal */}
          <BarraSemana total={fixosDoAluno.length} max={MAX_SEMANA} />

          <Legenda />

          <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: '3px', minWidth: isMobile ? 440 : 580 }}>
              <thead>
                <tr>
                  <th style={{ width: 56, padding: '0.25rem', fontSize: '0.78rem', color: colors.gray, textAlign: 'left', fontWeight: '600' }}>
                    Horário
                  </th>
                  {DIAS.map(d => {
                    const jaTemNoDia = diasComFixo.has(d.id);
                    return (
                      <th key={d.id} style={{
                        padding: '0.25rem 0.1rem',
                        textAlign: 'center',
                        fontSize: isMobile ? '0.78rem' : '0.85rem',
                        color: jaTemNoDia ? '#854d0e' : colors.dark,
                        fontWeight: '700',
                        minWidth: isMobile ? 52 : 70,
                      }}>
                        {isMobile ? d.abrev : d.nome.replace('-feira', '')}
                        {jaTemNoDia && <div style={{ fontSize: '0.6rem', color: '#eab308' }}>✓ fixo</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {HORARIOS.map(horario => (
                  <tr key={horario}>
                    <td style={{ padding: '0.2rem 0.4rem', fontSize: '0.78rem', fontWeight: '700', color: colors.gray, whiteSpace: 'nowrap' }}>
                      {horario}
                    </td>
                    {DIAS.map(dia => {
                      const celula        = slotMap[dia.id][horario];
                      const isFixo        = !!celula.alunoFixoId;
                      const isCheioSlot   = !isFixo && celula.count >= LIMITE_SLOT;
                      const isBloqDia     = !isFixo && !isCheioSlot && diasComFixo.has(dia.id);
                      const isBloqSemana  = !isFixo && !isCheioSlot && !isBloqDia && semanaCheia;
                      const carregando    = loadingCelula === `${dia.id}-${horario}`;

                      return (
                        <td key={dia.id} style={{ padding: 0 }}>
                          <CelulaGrade
                            isFixo={isFixo}
                            isCheioSlot={isCheioSlot}
                            isBloqDia={isBloqDia}
                            isBloqSemana={isBloqSemana}
                            temOutros={celula.temOutros}
                            count={celula.count}
                            carregando={carregando}
                            onClick={() => handleCelulaClick(dia.id, horario)}
                            isMobile={isMobile}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tags dos horários fixos do aluno */}
          {fixosDoAluno.length > 0 && (
            <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: `1px solid ${colors.grayLight}` }}>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.dark, marginBottom: '0.5rem' }}>
                Horários fixos de {alunoSelecionado.nome.split(' ')[0]}:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {fixosDoAluno
                  .sort((a, b) => a.dia_semana - b.dia_semana || normalizeHorario(a.horario).localeCompare(normalizeHorario(b.horario)))
                  .map(af => (
                    <span key={af.id} style={mkBadge('#fef9c3', '#854d0e', '#eab308')}>
                      ⭐ {DIAS.find(d => d.id === Number(af.dia_semana))?.abrev} {normalizeHorario(af.horario)}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Listagem geral ── */}
      {!alunoSelecionado && (
        <ListagemGeral
          alunosAgrupados={alunosAgrupados}
          loading={loading}
          maxSemana={MAX_SEMANA}
          onEditar={selecionarAluno}
          onReenviarAtivacao={handleReenviarAtivacao}
          onAbrirAplicar={abrirModalAplicar}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

// ─── Subcomponentes ───────────────────────────────────────────────────────────

function BarraSemana({ total, max }) {
  const pct = Math.min((total / max) * 100, 100);
  const cor  = total >= max ? '#dc2626' : total === max - 1 ? '#f59e0b' : '#16a34a';
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: '600', color: colors.gray, marginBottom: '4px' }}>
        <span>Aulas fixas na semana</span>
        <span style={{ color: cor }}>{total}/{max}</span>
      </div>
      <div style={{ height: 8, backgroundColor: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: cor, borderRadius: 4, transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
}

function Legenda() {
  const itens = [
    { bg: '#fef9c3', border: '#eab308',  label: 'Fixo — clique para remover' },
    { bg: '#f0fdf4', border: '#4ade80',  label: 'Disponível (outros fixos)' },
    { bg: '#f8fafc', border: '#cbd5e1',  label: 'Vazio' },
    { bg: '#fef2f2', border: '#f87171',  label: 'Slot cheio' },
    { bg: '#fff7ed', border: '#fb923c',  label: 'Já tem aula neste dia' },
    { bg: '#f5f3ff', border: '#a78bfa',  label: 'Semana cheia (3/3)' },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: '0.75rem', color: colors.gray }}>
      {itens.map(it => (
        <span key={it.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{
            display: 'inline-block',
            width: 14, height: 14,
            borderRadius: 3,
            backgroundColor: it.bg,
            border: `2px solid ${it.border}`,
            flexShrink: 0,
          }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function CelulaGrade({ isFixo, isCheioSlot, isBloqDia, isBloqSemana, temOutros, count, carregando, onClick, isMobile }) {
  let bg, border, icon, textColor, cursor, hoverBg, title;

  if (isFixo) {
    bg = '#fef9c3'; border = '#eab308'; icon = carregando ? '⏳' : '⭐';
    textColor = '#854d0e'; cursor = 'pointer'; hoverBg = '#fef08a';
    title = 'Clique para remover';
  } else if (isCheioSlot) {
    bg = '#fef2f2'; border = '#f87171'; icon = '🔒';
    textColor = '#dc2626'; cursor = 'not-allowed'; hoverBg = bg;
    title = `Slot cheio (${count}/${LIMITE_SLOT})`;
  } else if (isBloqDia) {
    bg = '#fff7ed'; border = '#fb923c'; icon = '—';
    textColor = '#c2410c'; cursor = 'not-allowed'; hoverBg = bg;
    title = 'Já existe uma aula fixa neste dia';
  } else if (isBloqSemana) {
    bg = '#f5f3ff'; border = '#a78bfa'; icon = '—';
    textColor = '#7c3aed'; cursor = 'not-allowed'; hoverBg = bg;
    title = 'Semana cheia (máx 3 aulas)';
  } else if (temOutros) {
    bg = '#f0fdf4'; border = '#4ade80'; icon = carregando ? '⏳' : '+';
    textColor = '#15803d'; cursor = 'pointer'; hoverBg = '#dcfce7';
    title = 'Clique para adicionar';
  } else {
    bg = '#f8fafc'; border = '#cbd5e1'; icon = carregando ? '⏳' : '+';
    textColor = colors.gray; cursor = 'pointer'; hoverBg = '#f1f5f9';
    title = 'Clique para adicionar';
  }

  const isDesabilitado = isCheioSlot || isBloqDia || isBloqSemana;
  const size = isMobile ? 50 : 62;

  return (
    <div
      onClick={!isDesabilitado && !carregando ? onClick : undefined}
      title={title}
      style={{
        width: size, height: size,
        borderRadius: '8px',
        border: `2px solid ${border}`,
        backgroundColor: bg,
        cursor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s ease',
        opacity: carregando ? 0.55 : 1,
        userSelect: 'none',
        margin: '1px auto',
      }}
      onMouseEnter={e => { if (!isDesabilitado) e.currentTarget.style.backgroundColor = hoverBg; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = bg; }}
    >
      <span style={{ fontSize: isMobile ? '0.9rem' : '1rem', lineHeight: 1 }}>{icon}</span>
      <span style={{ fontSize: '0.62rem', fontWeight: '700', color: textColor, marginTop: '2px' }}>
        {count}/{LIMITE_SLOT}
      </span>
    </div>
  );
}

function AlunoCard({ aluno, totalFixos, maxSemana, onTrocar, isMobile }) {
  const inicial = aluno.nome?.[0]?.toUpperCase() ?? '?';
  const cor = totalFixos >= maxSemana ? '#dc2626' : totalFixos === maxSemana - 1 ? '#f59e0b' : '#16a34a';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: `linear-gradient(135deg, ${colors.primary} 0%, #0f5a30 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: '700', fontSize: '1.25rem', flexShrink: 0,
        }}>
          {inicial}
        </div>
        <div>
          <div style={{ fontWeight: '700', fontSize: isMobile ? '1rem' : '1.1rem', color: colors.dark }}>
            {aluno.nome}
          </div>
          <div style={{ fontSize: '0.8rem', color: colors.gray }}>{aluno.email}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={mkBadge('#fef9c3', cor, '#eab308')}>
          ⭐ {totalFixos}/{maxSemana} aulas fixas
        </span>
        <button onClick={onTrocar} style={{ ...styles.button, ...styles.buttonOutline, ...styles.buttonSmall }}>
          Trocar Aluno
        </button>
      </div>
    </div>
  );
}

function BuscaAluno({ buscaTexto, setBuscaTexto, resultados, onSelecionar, isMobile }) {
  return (
    <div>
      <label style={{ ...styles.formLabel, fontSize: isMobile ? '0.95rem' : '1rem' }}>
        Buscar Aluno
      </label>
      <input
        type="text"
        value={buscaTexto}
        onChange={e => setBuscaTexto(e.target.value)}
        placeholder="Digite o nome ou email do aluno..."
        style={{ ...styles.formInput, fontSize: isMobile ? '0.95rem' : '1rem' }}
        autoFocus
      />
      {resultados.length > 0 && (
        <div style={{
          marginTop: '0.5rem',
          border: `1px solid ${colors.grayLight}`,
          borderRadius: '0.5rem',
          maxHeight: 200,
          overflowY: 'auto',
          backgroundColor: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {resultados.map(aluno => (
            <div
              key={aluno.id}
              onClick={() => onSelecionar(aluno)}
              style={{ padding: '0.75rem 1rem', cursor: 'pointer', borderBottom: `1px solid ${colors.grayLight}` }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = colors.grayLight; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{ fontWeight: '600', fontSize: isMobile ? '0.95rem' : '1rem' }}>{aluno.nome}</div>
              <div style={{ fontSize: '0.8rem', color: colors.gray }}>{aluno.email}</div>
            </div>
          ))}
        </div>
      )}
      {buscaTexto.length >= 2 && resultados.length === 0 && (
        <div style={{ marginTop: '0.5rem', color: colors.gray, fontSize: '0.875rem' }}>
          Nenhum aluno encontrado
        </div>
      )}
    </div>
  );
}

// ─── Modal Aplicar Fixos ──────────────────────────────────────────────────────
function ModalAplicarFixos({
  semanaRef, sabado, totalFixos,
  loadingAplicar, resultadoAplicar,
  onNavAnterior, onNavProxima, onAplicar, onFechar, isMobile
}) {
  const btnNav = {
    background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.4rem', padding: '0.35rem 0.65rem',
    cursor: 'pointer', fontSize: '1rem', lineHeight: 1,
    color: '#334155', transition: 'background 0.15s',
  };
  const resultado = resultadoAplicar;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 3000, padding: isMobile ? '12px' : '0',
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px',
        width: isMobile ? '100%' : '520px',
        maxWidth: '95vw', maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.3rem', fontWeight: '700', color: '#0f172a' }}>
            ⚡ Aplicar Alunos Fixos nas Vagas
          </h3>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>

        {/* Info */}
        <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: '0.85rem', color: '#0369a1' }}>
          <strong>O que faz:</strong> Para cada aluno fixo cadastrado, procura uma vaga livre no horário
          correspondente da semana selecionada e atribui o fixo a ela (sem apagar vagas existentes).
        </div>

        {/* Seletor de semana */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: '700', color: '#334155', fontSize: '0.9rem', marginBottom: '10px' }}>
            Semana alvo
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
            <button style={btnNav} onClick={onNavAnterior} title="Semana anterior">◀</button>
            <div style={{
              flex: 1, textAlign: 'center',
              padding: '10px 16px',
              backgroundColor: '#f8fafc',
              border: '2px solid #6366f1',
              borderRadius: '10px',
              fontWeight: '700', fontSize: isMobile ? '0.9rem' : '1rem',
              color: '#0f172a',
            }}>
              📅 {fmtData(semanaRef)} — {fmtData(sabado)}
            </div>
            <button style={btnNav} onClick={onNavProxima} title="Próxima semana">▶</button>
          </div>
          <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '0.78rem', color: '#64748b' }}>
            {totalFixos} regra{totalFixos !== 1 ? 's' : ''} de fixos cadastrada{totalFixos !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Resultado da aplicação */}
        {resultado && !resultado.error && (
          <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ResultadoBloco
              cor="#10b981" bg="#f0fdf4" borda="#86efac"
              titulo={`✅ ${resultado.aplicados?.length || 0} fixo(s) aplicado(s)`}
              itens={resultado.aplicados}
            />
            <ResultadoBloco
              cor="#64748b" bg="#f8fafc" borda="#cbd5e1"
              titulo={`ℹ️ ${resultado.ja_existiam?.length || 0} já existiam`}
              itens={resultado.ja_existiam}
            />
            {resultado.sem_vaga?.length > 0 && (
              <ResultadoBloco
                cor="#dc2626" bg="#fef2f2" borda="#fca5a5"
                titulo={`⚠️ ${resultado.sem_vaga.length} sem vaga disponível`}
                itens={resultado.sem_vaga}
              />
            )}
          </div>
        )}
        {resultado?.error && (
          <div style={{ ...alertStyle('error'), marginBottom: '16px' }}>{resultado.error}</div>
        )}

        {/* Botões */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onFechar}
            style={{
              padding: '0.6rem 1.2rem', border: '2px solid #cbd5e1',
              borderRadius: '8px', background: 'transparent',
              color: '#64748b', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem',
            }}
          >
            {resultado && !resultado.error ? 'Fechar' : 'Cancelar'}
          </button>
          <button
            onClick={onAplicar}
            disabled={loadingAplicar}
            style={{
              padding: '0.6rem 1.4rem', border: 'none',
              borderRadius: '8px',
              background: loadingAplicar ? '#a78bfa' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: '#fff', fontWeight: '700', cursor: loadingAplicar ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            {loadingAplicar ? '⏳ Aplicando...' : '⚡ Aplicar Fixos'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResultadoBloco({ cor, bg, borda, titulo, itens }) {
  const [expandido, setExpandido] = useState(false);
  if (!itens || itens.length === 0) return null;
  return (
    <div style={{ border: `1px solid ${borda}`, borderRadius: '8px', overflow: 'hidden' }}>
      <button
        onClick={() => setExpandido(e => !e)}
        style={{
          width: '100%', padding: '8px 12px', background: bg,
          border: 'none', cursor: 'pointer', textAlign: 'left',
          fontWeight: '700', fontSize: '0.85rem', color: cor,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        {titulo}
        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{expandido ? '▲ fechar' : '▼ ver'}</span>
      </button>
      {expandido && (
        <div style={{ padding: '8px 12px', background: '#fff', maxHeight: '160px', overflowY: 'auto' }}>
          {itens.map((item, i) => (
            <div key={i} style={{ fontSize: '0.8rem', color: '#334155', padding: '2px 0', borderBottom: '1px solid #f1f5f9' }}>
              <strong>{item.aluno}</strong> — {item.dia} às {String(item.hora).substring(0, 5)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ListagemGeral({ alunosAgrupados, loading, maxSemana, onEditar, onReenviarAtivacao, onAbrirAplicar, isMobile }) {
  return (
    <div style={{ ...styles.card, padding: isMobile ? '1rem' : '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <h4 style={{ margin: 0, color: colors.dark, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
          Alunos Fixos Cadastrados
          <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', fontWeight: '400', color: colors.gray }}>
            ({alunosAgrupados.length} aluno{alunosAgrupados.length !== 1 ? 's' : ''})
          </span>
        </h4>
        <button
          onClick={onAbrirAplicar}
          disabled={alunosAgrupados.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.55rem 1.1rem',
            backgroundColor: alunosAgrupados.length === 0 ? '#e2e8f0' : '#7c3aed',
            color: alunosAgrupados.length === 0 ? '#94a3b8' : '#fff',
            border: 'none', borderRadius: '0.5rem',
            fontWeight: '700', fontSize: isMobile ? '0.82rem' : '0.9rem',
            cursor: alunosAgrupados.length === 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
          }}
          title="Aplicar regras de alunos fixos a uma semana existente"
        >
          ⚡ Aplicar nas Vagas
        </button>
      </div>

      {loading && alunosAgrupados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray }}>Carregando...</div>
      ) : alunosAgrupados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray }}>
          Nenhum aluno fixo cadastrado. Busque um aluno acima para começar.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {alunosAgrupados.map(aluno => {
            const total = aluno.horarios.length;
            const cor = total >= maxSemana ? '#dc2626' : '#15803d';
            return (
              <div
                key={aluno.id}
                style={{
                  padding: '0.875rem 1rem',
                  backgroundColor: colors.grayLight,
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '700', color: colors.dark, fontSize: isMobile ? '0.95rem' : '1rem' }}>
                      {aluno.nome}
                    </span>
                    <span style={mkBadge('#fef9c3', cor, '#eab308')}>
                      ⭐ {total}/{maxSemana}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: colors.gray, marginBottom: '0.4rem' }}>{aluno.email}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {aluno.horarios
                      .sort((a, b) => a.dia_semana - b.dia_semana || a.horario.localeCompare(b.horario))
                      .map(h => (
                        <span key={h.id} style={mkBadge('#fef9c3', '#854d0e', '#eab308')}>
                          ⭐ {DIAS.find(d => d.id === h.dia_semana)?.abrev} {h.horario}
                        </span>
                      ))}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                  <button
                    onClick={() => onEditar({ id: aluno.id, nome: aluno.nome, email: aluno.email })}
                    style={{ ...styles.button, ...styles.buttonOutline, ...styles.buttonSmall }}
                  >
                    Editar
                  </button>
                  {aluno.auth_type === 'local' && aluno.email && (
                    <button
                      onClick={() => onReenviarAtivacao(aluno)}
                      style={{ ...styles.button, ...styles.buttonSmall, backgroundColor: aluno.password_set ? '#f59e0b' : '#3b82f6', color: '#fff', border: 'none' }}
                      title={aluno.password_set ? 'Enviar link para redefinir a senha' : 'Enviar link de ativação de conta'}
                    >
                      {aluno.password_set ? '🔑 Resetar senha' : '📧 Gerar senha'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
